import { useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Card, ErrorState, Loading } from '../components/ui.jsx';
import { DocumentActions } from '../components/DocumentActions.jsx';
import { IconEdit } from '../components/Icons.jsx';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { paymentTypeReceipt, t } from '../lib/i18n.js';
import { formatDate, liters, money, tons } from '../lib/format.js';
import { purchaseReceiptText } from '../lib/whatsapp.js';

export default function PurchaseReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useAuth();
  const { data: purchase, error, loading, reload } = useApi(`/api/purchases/${id}`);
  const docRef = useRef(null);

  if (loading && !purchase) return <Loading />;
  if (error) {
    return (
      <>
        <PageHeader title={t.doc.purchaseReceipt} back="/buy" />
        <div className="page">
          <ErrorState error={error} onRetry={reload} />
        </div>
      </>
    );
  }
  if (!purchase) return null;

  const amount = purchase.currency === 'IQD' ? purchase.amount_iqd : purchase.amount_usd;
  const other = purchase.currency === 'IQD' ? purchase.amount_usd : purchase.amount_iqd;

  return (
    <>
      <PageHeader
        title={t.doc.purchaseReceipt}
        subtitle={purchase.batch_no}
        back="/buy"
        actions={
          <button type="button" className="btn btn-sm btn-ghost no-print" onClick={() => navigate(`/buy/${id}/edit`)}>
            <IconEdit /> {t.common.edit}
          </button>
        }
      />

      <div className="page">
        <DocumentActions
          targetRef={docRef}
          filename={`purchase-${purchase.batch_no}.pdf`}
          whatsappText={purchaseReceiptText(purchase, settings)}
          phone={purchase.supplier_phone}
          countryCode={settings.whatsapp_country_code || '964'}
        />

        <div className="doc" ref={docRef}>
          <div className="doc-head">
            <img src="/logo.png" alt="" className="doc-logo" />
            <div className="brand">
              <div className="name">{settings.facility_name || t.app.name}</div>
              <div className="contact">
                {settings.facility_address}
                {settings.facility_address && settings.facility_phone ? ' · ' : ''}
                {settings.facility_phone}
              </div>
            </div>
            <div className="ref">
              <div className="kind">{t.doc.purchaseReceipt}</div>
              <div className="no">{purchase.batch_no}</div>
              <div className="contact">{formatDate(purchase.purchased_on)}</div>
            </div>
          </div>

          <div className="doc-rows">
            <Row k={t.purchase.supplier} v={purchase.supplier_name} />
            {purchase.supplier_phone && <Row k={t.common.phone} v={purchase.supplier_phone} />}
            <Row k={t.common.quantity} v={`${tons(purchase.tons)}  (${liters(purchase.liters)})`} />
            {Number(purchase.price_per_ton) > 0 && (
              <Row k={t.purchase.rate} v={money(purchase.price_per_ton, purchase.currency)} />
            )}
            <Row k={t.purchase.destinationTank} v={purchase.tank_name} />
            <Row k={t.shared.payment} v={paymentTypeReceipt[purchase.payment_type]} />
            {Number(other) > 0 && (
              <Row k={t.shared.reference} v={money(other, purchase.currency === 'IQD' ? 'USD' : 'IQD')} />
            )}
            {purchase.notes && <Row k={t.common.notes} v={purchase.notes} />}
          </div>

          <div className="doc-total">
            <span className="k">{t.common.total}</span>
            <span className="v">{money(amount, purchase.currency)}</span>
          </div>

          <div className="doc-sign">
            <div>{t.purchase.deliveredBy}</div>
            <div>{t.purchase.receivedBy}</div>
          </div>

          <div className="doc-foot">
            <span>{settings.receipt_footer || ''}</span>
            <span className="issued">
              {t.shared.issued(formatDate(purchase.created_at?.slice(0, 10)))}
              {purchase.created_by_name ? ` ${t.shared.issuedBy(purchase.created_by_name)}` : ''}
            </span>
          </div>
        </div>

        <Card>
          <div className="btn-row no-print">
            <Link className="btn" to={`/suppliers/${purchase.supplier_id}`}>
              {t.purchase.supplierStatement}
            </Link>
            <Link className="btn" to="/buy/new">
              {t.purchase.another}
            </Link>
          </div>
        </Card>
      </div>
    </>
  );
}

const Row = ({ k, v }) => (
  <div className="doc-row">
    <span className="k">{k}</span>
    <span className="v">{v}</span>
  </div>
);
